import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Search, Filter, ShoppingCart, Star, Eye, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Product } from '../admin/types';
import toast from 'react-hot-toast';
import { trackProductView, trackAddToCart, trackSearch } from '../utils/analytics';
import CATEGORY_TREE from '../data/categories';
import MakeOfferButton from './MakeOfferButton';

const OnlineShop: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const location = useLocation();

  const { addToCart, toggleCart, state } = useCart();

  const [primaryCategory, setPrimaryCategory] = useState('all');
  const [secondaryCategory, setSecondaryCategory] = useState('');
  // Build quick lookup maps for category value -> label to make categories searchable
  const categoryLabelMap = React.useMemo(() => {
    const map = new Map<string, string>();
    CATEGORY_TREE.forEach((c) => {
      map.set(c.value, c.label);
      (c.children || []).forEach((s) => map.set(s.value, s.label));
    });
    return map;
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle deep linking to products
  useEffect(() => {
    if (products.length > 0) {
      const params = new URLSearchParams(location.search);
      const productId = params.get('productId');
      if (productId) {
        const product = products.find(p => p.id === productId);
        if (product) {
          setSelectedProduct(product);
        }
      }
    }
  }, [products, location.search]);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, searchTerm, primaryCategory, secondaryCategory, sortBy]);

  // Track search events
  useEffect(() => {
    if (searchTerm.length > 2) { // Only track meaningful searches
      const timeoutId = setTimeout(() => {
        trackSearch(searchTerm, filteredProducts.length);
      }, 500); // Debounce search tracking

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, filteredProducts.length]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { supabase } = await import('../admin/lib/supabaseClient');
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true) // Show all active products
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = () => {
    const q = searchTerm.trim().toLowerCase();
    let filtered = products.filter(product => {
      const matchesSearch = q === '' || (
        product.name.toLowerCase().includes(q) ||
        (product.description?.toLowerCase().includes(q) ?? false) ||
        // search against stored category values and their labels
        ((product as any).category_primary && String((product as any).category_primary).toLowerCase().includes(q)) ||
        ((product as any).category_secondary && String((product as any).category_secondary).toLowerCase().includes(q)) ||
        (categoryLabelMap.get((product as any).category_primary || '') || '').toLowerCase().includes(q) ||
        (categoryLabelMap.get((product as any).category_secondary || '') || '').toLowerCase().includes(q)
      );
      const matchesCategory = primaryCategory === 'all' ||
        // match primary/secondary category fields saved on product row
        (product as any).category_primary === primaryCategory ||
        (secondaryCategory && (product as any).category_secondary === secondaryCategory) ||
        // fallback: include product names that mention the primary category or its label
        product.name.toLowerCase().includes(primaryCategory.toLowerCase()) ||
        (categoryLabelMap.get(primaryCategory) || '').toLowerCase().includes(primaryCategory.toLowerCase());
      return matchesSearch && matchesCategory;
    });

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  };

  const handleAddToCart = (product: Product, quantity: number = 1) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      stock_level: product.stock_level,
      quantity
    });

    // Track add to cart event
    trackAddToCart(product.id, product.name, quantity, product.price);
  };

  const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
    const [quantity, setQuantity] = useState(1);

    return (
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
        whileHover={{ y: -5 }}
        layout
      >
        {/* Product Image */}
        <div className="relative aspect-square overflow-hidden bg-gray-100 dark:bg-gray-700">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingCart size={48} className="text-gray-400" />
            </div>
          )}

          {/* Quick View Button */}
          <button
            onClick={() => {
              setSelectedProduct(product);
              trackProductView(product.id, product.name);
            }}
            className="absolute top-3 right-3 p-2 bg-white/90 dark:bg-gray-800/90 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-800"
          >
            <Eye size={16} className="text-gray-600 dark:text-gray-300" />
          </button>

          {/* Stock Badge */}
          {product.stock_level && product.stock_level <= 5 && (
            <div className="absolute top-3 left-3 px-2 py-1 bg-orange-500 text-white text-xs rounded-full">
              Low Stock: {product.stock_level}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
            {product.name}
          </h3>

          {product.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
              {product.description}
            </p>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className="text-2xl font-bold text-mini-red">
              R {product.price.toFixed(2)}
            </div>
            <div className="flex items-center gap-1">
              <Star size={16} className="text-yellow-400 fill-current" />
              <span className="text-sm text-gray-600 dark:text-gray-400">4.8</span>
            </div>
          </div>

          {/* Quantity and Add to Cart */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Minus size={14} />
                </button>
                <span className="px-3 py-2 font-medium min-w-[3rem] text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock_level || 99, quantity + 1))}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Plus size={14} />
                </button>
              </div>

              <button
                onClick={() => handleAddToCart(product, quantity)}
                className="flex-1 bg-gradient-to-r from-mini-red to-mini-green text-white py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-200 font-medium"
              >
                Add to Cart
              </button>
            </div>

            {(product as any).allow_offers && (
              <MakeOfferButton
                itemId={product.id}
                itemType="product"
                itemName={product.name}
                itemPrice={Math.round(product.price * 100)}
                allowOffers={(product as any).allow_offers}
              />
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const ProductModal: React.FC<{ product: Product; onClose: () => void }> = ({ product, onClose }) => {
    const [quantity, setQuantity] = useState(1);

    return (
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid md:grid-cols-2 gap-6 p-6">
              {/* Product Image */}
              <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingCart size={64} className="text-gray-400" />
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {product.name}
                </h2>

                {product.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {product.description}
                  </p>
                )}

                <div className="text-3xl font-bold text-mini-red mb-4">
                  R {product.price.toFixed(2)}
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Stock:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${(product.stock_level || 0) > 10
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : (product.stock_level || 0) > 0
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                    {product.stock_level || 0} available
                  </span>
                </div>

                {/* Quantity and Add to Cart */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Quantity:
                    </span>
                    <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="px-4 py-2 font-medium min-w-[4rem] text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(Math.min(product.stock_level || 99, quantity + 1))}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          handleAddToCart(product, quantity);
                          onClose();
                        }}
                        className="flex-1 bg-gradient-to-r from-mini-red to-mini-green text-white py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-200 font-medium"
                      >
                        Add to Cart - R {(product.price * quantity).toFixed(2)}
                      </button>
                      <button
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        Close
                      </button>
                    </div>

                    {(product as any).allow_offers && (
                      <MakeOfferButton
                        itemId={product.id}
                        itemType="product"
                        itemName={product.name}
                        itemPrice={Math.round(product.price * 100)}
                        allowOffers={(product as any).allow_offers}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <div className="min-h-[80vh] md:min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">
              Gerrie's Classic Minis Shop
            </h1>
            <button
              onClick={toggleCart}
              className="relative p-2 bg-mini-red text-white rounded-lg hover:bg-mini-red/90 transition-colors"
            >
              <ShoppingCart size={20} />
              {state.totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-mini-green text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {state.totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white shadow-sm p-6 mb-8 rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Category Filter (two-level) */}
            <div className="relative grid grid-cols-2 gap-2">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <select
                  value={primaryCategory}
                  onChange={(e) => { setPrimaryCategory(e.target.value); setSecondaryCategory(''); }}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none"
                >
                  {CATEGORY_TREE.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={secondaryCategory}
                  onChange={(e) => setSecondaryCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none"
                >
                  <option value="">(Any subcategory)</option>
                  {(CATEGORY_TREE.find(c => c.value === primaryCategory)?.children || []).map(sub => (
                    <option key={sub.value} value={sub.value}>{sub.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="name">Sort by Name</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="newest">Newest First</option>
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            {loading ? 'Loading...' : `${filteredProducts.length} products found`}
            {searchTerm && ` for "${searchTerm}"`}
          </p>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 animate-pulse">
                <div className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No products found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm ? 'Try adjusting your search or filters' : 'Products coming soon!'}
            </p>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            layout
          >
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </motion.div>
        )}
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
};

export default OnlineShop;
